import { useCallback, useState } from 'react';
import { TFunction } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Section from '../../../componentsv2/Section';
import Table from '../../../componentsv2/Table';
import TableFilters from '../../../components/TableFilters';
import Typography from '../../../componentsv2/Typography';
import Tooltip from '../../../componentsv2/Tooltip';
import { useSnackbar } from '../../../context';
import { useCompanies } from '../../../hooks/useCompanies';
import { formatDate } from '../../../utils/common';
import { allCompaniesTableFilters } from '../../../constants/common';
import { CompanyAdminFilters, CompanyAdminResult } from '../../../types/companyUser';

export const MiniInvoiceFinancingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      opacity="0.4"
      d="M17.5 5.83464V14.168C17.5 16.668 16.25 18.3346 13.3333 18.3346H6.66667C3.75 18.3346 2.5 16.668 2.5 14.168V5.83464C2.5 3.33464 3.75 1.66797 6.66667 1.66797H13.3333C16.25 1.66797 17.5 3.33464 17.5 5.83464Z"
      fill="#8DA6FF"
    />
    <path
      d="M15.4167 7.70833H13.75C12.4834 7.70833 11.4584 6.68333 11.4584 5.41667V3.75C11.4584 3.40833 11.7417 3.125 12.0834 3.125C12.425 3.125 12.7084 3.40833 12.7084 3.75V5.41667C12.7084 5.99167 13.175 6.45833 13.75 6.45833H15.4167C15.7584 6.45833 16.0417 6.74167 16.0417 7.08333C16.0417 7.425 15.7584 7.70833 15.4167 7.70833Z"
      fill="#161B9B"
    />
    <path
      d="M9.99996 11.457H6.66663C6.32496 11.457 6.04163 11.1737 6.04163 10.832C6.04163 10.4904 6.32496 10.207 6.66663 10.207H9.99996C10.3416 10.207 10.625 10.4904 10.625 10.832C10.625 11.1737 10.3416 11.457 9.99996 11.457Z"
      fill="#161B9B"
    />
    <path
      d="M13.3333 14.793H6.66663C6.32496 14.793 6.04163 14.5096 6.04163 14.168C6.04163 13.8263 6.32496 13.543 6.66663 13.543H13.3333C13.675 13.543 13.9583 13.8263 13.9583 14.168C13.9583 14.5096 13.675 14.793 13.3333 14.793Z"
      fill="#161B9B"
    />
  </svg>
);

export const MiniBankingIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      opacity="0.4"
      d="M10.3083 1.79219L17.8083 4.79217C18.1 4.90883 18.3333 5.25883 18.3333 5.56716V8.33383C18.3333 8.79217 17.9583 9.16717 17.5 9.16717H2.49996C2.04163 9.16717 1.66663 8.79217 1.66663 8.33383V5.56716C1.66663 5.25883 1.89996 4.90883 2.19163 4.79217L9.69163 1.79219C9.8583 1.72552 10.1416 1.72552 10.3083 1.79219Z"
      fill="#0C6A65"
    />
    <path
      opacity="0.4"
      d="M18.3333 18.3333H1.66663V15.8333C1.66663 15.375 2.04163 15 2.49996 15H17.5C17.9583 15 18.3333 15.375 18.3333 15.8333V18.3333Z"
      fill="#0C6A65"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.21688 9.16797V15.0013H3.13354V9.16797H5.21688ZM9.10034 9.16797V15.0013H7.01701V9.16797H9.10034ZM12.983 9.16797V15.0013H10.8997V9.16797H12.983ZM16.8665 9.16797V15.0013H14.7831V9.16797H16.8665Z"
      fill="#0C6A65"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.208374 18.332C0.208374 17.9869 0.488196 17.707 0.833374 17.707H19.1667C19.5119 17.707 19.7917 17.9869 19.7917 18.332C19.7917 18.6772 19.5119 18.957 19.1667 18.957H0.833374C0.488196 18.957 0.208374 18.6772 0.208374 18.332Z"
      fill="#0C6A65"
    />
    <path
      d="M10 7.08203C10.6904 7.08203 11.25 6.52239 11.25 5.83203C11.25 5.14168 10.6904 4.58203 10 4.58203C9.30964 4.58203 8.75 5.14168 8.75 5.83203C8.75 6.52239 9.30964 7.08203 10 7.08203Z"
      fill="#CAF2F0"
    />
  </svg>
);

export const MiniComplianceIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      opacity="0.4"
      d="M9.99996 18.3346C14.6023 18.3346 18.3333 14.6037 18.3333 10.0013C18.3333 5.39893 14.6023 1.66797 9.99996 1.66797C5.39759 1.66797 1.66663 5.39893 1.66663 10.0013C1.66663 14.6037 5.39759 18.3346 9.99996 18.3346Z"
      fill="#6DCEEF"
    />
    <path
      d="M12.5 13.3346C12.0333 13.3346 11.6583 12.9596 11.6583 12.5013C11.6583 12.043 12.0333 11.668 12.4917 11.668C12.95 11.668 13.325 12.043 13.325 12.5013C13.325 12.9596 12.9583 13.3346 12.5 13.3346Z"
      fill="#0B607D"
    />
    <path
      d="M7.5083 8.33464C7.04163 8.33464 6.66663 7.95964 6.66663 7.5013C6.66663 7.04297 7.04163 6.66797 7.49996 6.66797C7.95829 6.66797 8.33329 7.04297 8.33329 7.5013C8.33329 7.95964 7.96663 8.33464 7.5083 8.33464Z"
      fill="#0B607D"
    />
    <path
      d="M7.50002 13.1268C7.34168 13.1268 7.18333 13.0685 7.05833 12.9435C6.81666 12.7019 6.81666 12.3018 7.05833 12.0602L12.0583 7.06016C12.3 6.81849 12.7 6.81849 12.9417 7.06016C13.1834 7.30182 13.1834 7.70187 12.9417 7.94354L7.94171 12.9435C7.81671 13.0685 7.65835 13.1268 7.50002 13.1268Z"
      fill="#0B607D"
    />
  </svg>
);

const INITIAL_PAGE = 1;

export default function AllCompanies({ t }: { t: TFunction }) {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [filters, setFilters] = useState<CompanyAdminFilters>({
    company: '',
    ordering: '',
    products: [],
    subProducts: [],
    page: INITIAL_PAGE,
    pageSize: 10,
  });

  const handleError = useCallback(
    (errorCode?: string) => {
      showSnackbar('There was an error. Please try again later.', 'error');
    },
    [showSnackbar]
  );

  const { data: companiesData, isLoading } = useCompanies(filters, handleError);

  const handleApplyFilters = (newFilters: any) => {
    setFilters({ ...newFilters, page: INITIAL_PAGE });
  };
  return (
    <Section sx={{ backgroundColor: '#fff', padding: '1em 2em' }}>
      <Grid container rowSpacing={2}>
        <Grid item xs={12}>
          <TableFilters
            showLabel={false}
            showFilteringButtons={false}
            filterOptions={allCompaniesTableFilters}
            applyFilters={(filters: any) => handleApplyFilters(filters)}
            clearFilters={null}
            initialValues={filters}
          />
        </Grid>
        <Grid item xs={12}>
          <Table
            emptyMessageTitle={t(`admin:clientsPage.emptyMessageTitle`)}
            data={companiesData?.results ?? []}
            isLoading={isLoading}
            onClickRow={(row: CompanyAdminResult) => {
              navigate(`/companies/${row.uuid}`);
            }}
            paginationSettings={{
              count: companiesData?.count ?? 0,
              showPages: true,
              handlePageChange: (_, newPage) => {
                setFilters((prevFilters) => ({ ...prevFilters, page: newPage + 1 }));
              },
              page: filters.page - 1,
              rowsPerPage: filters.pageSize,
            }}
            columns={[
              {
                field: 'uuid',
                headerName: t(`admin:clientsPage.columnOptions.companyId`),
              },
              {
                field: 'userName',
                headerName: t(`admin:clientsPage.columnOptions.user`),
              },
              {
                field: 'name',
                headerName: t(`admin:clientsPage.columnOptions.company`),
              },
              {
                field: 'country',
                headerName: t(`admin:clientsPage.columnOptions.country`),
                renderCell: (row: CompanyAdminResult) => {
                  return <Typography>{t(`user:preapproval.location.options.${row.country}`)}</Typography>;
                },
              },
              {
                field: 'updatedAt',
                headerName: t(`admin:clientsPage.columnOptions.dateModified`),
                renderCell: ({ updatedAt }: CompanyAdminResult) => {
                  return (
                    <Typography>
                      {formatDate(
                        new Date(updatedAt),
                        'en-US',
                        {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                        },
                        false
                      )}
                    </Typography>
                  );
                },
              },
              {
                field: 'products',
                headerName: t(`admin:clientsPage.columnOptions.products`),
                renderCell: ({ productStatus }: CompanyAdminResult) => {
                  if (!productStatus) return <></> ;
                  const hasInvoiceFinancingRequest =
                    productStatus.hasInvoiceFinancingRequest || productStatus.readyForFactoring;
                  const hasBankingRequest = productStatus.hasBankingRequest || productStatus.readyForBanking;
                  const hasComplianceRequest =
                    productStatus.hasComplianceRequest || productStatus.readyForCompliance;
                  return (
                    <Box sx={{ display: 'flex', gap: '0.5em' }}>
                      {!hasInvoiceFinancingRequest && !hasBankingRequest && !hasComplianceRequest && (
                        <Typography>
                          {t('admin:clientsPage.gridFilters.productsSelectOptions.none')}
                        </Typography>
                      )}
                      {hasInvoiceFinancingRequest && (
                        <Tooltip
                          title={t('admin:clientsPage.gridFilters.productsSelectOptions.invoice_financing')}
                        >
                          <Box>
                            <MiniInvoiceFinancingIcon />
                          </Box>
                        </Tooltip>
                      )}
                      {hasBankingRequest && (
                        <Tooltip title={t('admin:clientsPage.gridFilters.productsSelectOptions.banking')}>
                          <Box>
                            <MiniBankingIcon />
                          </Box>
                        </Tooltip>
                      )}
                      {hasComplianceRequest && (
                        <Tooltip title={t('admin:clientsPage.gridFilters.productsSelectOptions.compliance')}>
                          <Box>
                            <MiniComplianceIcon />
                          </Box>
                        </Tooltip>
                      )}
                    </Box>
                  );
                },
              },
            ]}
          />
        </Grid>
      </Grid>
    </Section>
  );
}
